import { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X, Upload, Image as ImageIcon, Loader2, Link2, FolderOpen, GripVertical } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { type ArchitectureImageUrls, getFallbackUrl } from '@/lib/architecture-drawings';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Check for local optimizer URL (for development)
// When set, images are sent directly to localhost optimizer instead of Edge Function
const LOCAL_OPTIMIZER_URL = import.meta.env.VITE_LOCAL_OPTIMIZER_URL;

interface ImageResponse {
  url: string;
  path?: string;
  width: number;
  height?: number;
  format?: string;
}

interface OptimizeResponse {
  success?: boolean;
  images?: ImageResponse[];
  urls?: Record<string, string>;
  sizes?: Record<string, number>;
  message?: string;
  error?: string;
}

interface LinkedImage {
  label: string;
  thumbnailUrl: string;
  imageUrls?: ArchitectureImageUrls;
  isLinked: true;
}

interface ImageUploadSectionProps {
  unitId: string;
  imageType: 'gallery' | 'floor_plan' | 'render';
  existingImages: string[];
  onImagesUpdated: (newImages: string[]) => void;
  maxImages?: number;
  label?: string;
  linkedImages?: LinkedImage[];
}

// Sortable Image Item Component
interface SortableImageItemProps {
  id: string;
  url: string;
  index: number;
  displayLabel: string;
  onRemove: (index: number) => void;
}

function SortableImageItem({ id, url, index, displayLabel, onRemove }: SortableImageItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      {/* Drag Handle */}
      <button
        type="button"
        className="absolute top-2 left-2 h-8 w-8 bg-black/60 hover:bg-black/80 text-white rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing z-10"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <img
        src={url}
        alt={`${displayLabel} ${index + 1}`}
        className="w-full h-32 object-cover rounded-lg border border-border"
      />
      <Button
        type="button"
        variant="destructive"
        size="icon"
        className="absolute top-2 right-2 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={() => onRemove(index)}
      >
        <X className="h-4 w-4" />
      </Button>
      {/* Position indicator */}
      <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
        {index + 1}
      </div>
    </div>
  );
}

export function ImageUploadSection({
  unitId,
  imageType,
  existingImages,
  onImagesUpdated,
  maxImages = 10,
  label,
  linkedImages = [],
}: ImageUploadSectionProps) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedLinkedImage, setSelectedLinkedImage] = useState<LinkedImage | null>(null);
  const [addByUrlValue, setAddByUrlValue] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerFolders, setPickerFolders] = useState<string[]>([]);
  const [pickerFolder, setPickerFolder] = useState<string>('');
  const [pickerFiles, setPickerFiles] = useState<{ url: string; name: string }[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const { toast } = useToast();

  // DnD Kit sensors for drag and drop reordering
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end to reorder images
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = existingImages.findIndex((url) => url === active.id);
      const newIndex = existingImages.findIndex((url) => url === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newImages = arrayMove(existingImages, oldIndex, newIndex);
        onImagesUpdated(newImages);
        toast({
          title: 'Images reordered',
          description: 'Save the unit to keep the new order.',
        });
      }
    }
  }, [existingImages, onImagesUpdated, toast]);

  const storagePathForType = imageType === 'floor_plan' ? 'floor-plan' : imageType;

  const loadPickerFolders = useCallback(async () => {
    setPickerLoading(true);
    try {
      const { data, error } = await supabase.storage.from('unit-images').list('', { limit: 200 });
      if (error) throw error;
      const names = (data || []).map((item) => item.name).filter((n) => n && !n.startsWith('.'));
      const sorted = names.sort();
      setPickerFolders(sorted);
      if (sorted.length > 0) setPickerFolder(sorted[0]);
    } catch (err) {
      toast({
        title: 'Could not list folders',
        description: err instanceof Error ? err.message : 'Storage list failed',
        variant: 'destructive',
      });
    } finally {
      setPickerLoading(false);
    }
  }, [toast]);

  const loadPickerFiles = useCallback(
    async (folder: string) => {
      if (!folder) {
        setPickerFiles([]);
        return;
      }
      setPickerLoading(true);
      try {
        const path = `${folder}/${storagePathForType}`;
        const { data, error } = await supabase.storage.from('unit-images').list(path, { limit: 500 });
        if (error) {
          setPickerFiles([]);
          return;
        }
        const files = (data || []).filter((f) => f.name && !f.name.endsWith('/'));
        const withUrl = files.map((f) => {
          const fullPath = `${path}/${f.name}`;
          const { data: urlData } = supabase.storage.from('unit-images').getPublicUrl(fullPath);
          return { url: urlData.publicUrl, name: f.name };
        });
        const byBase = new Map<string, { url: string; width: number }>();
        for (const { url, name } of withUrl) {
          const match = name.match(/^(.+)-(\d+)\.(avif|webp|jpg|jpeg|png)$/i);
          const base = match ? match[1] : name;
          const width = match ? parseInt(match[2], 10) : 0;
          const cur = byBase.get(base);
          if (!cur || width > cur.width) byBase.set(base, { url, width });
        }
        setPickerFiles(
          Array.from(byBase.entries()).map(([base, { url }]) => ({ url, name: base }))
        );
      } catch {
        setPickerFiles([]);
      } finally {
        setPickerLoading(false);
      }
    },
    [storagePathForType]
  );

  const onOpenPicker = useCallback(() => {
    setPickerOpen(true);
    setPickerFolder('');
    setPickerFiles([]);
    loadPickerFolders();
  }, [loadPickerFolders]);

  const onPickerFolderChange = useCallback(
    (folder: string) => {
      setPickerFolder(folder);
      loadPickerFiles(folder);
    },
    [loadPickerFiles]
  );

  useEffect(() => {
    if (pickerOpen && pickerFolder) loadPickerFiles(pickerFolder);
  }, [pickerOpen, pickerFolder, loadPickerFiles]);

  const handlePickImage = (url: string) => {
    if (existingImages.includes(url)) {
      toast({
        title: 'Already added',
        description: 'This image is already in the list.',
        variant: 'destructive',
      });
      return;
    }
    if (existingImages.length >= maxImages) {
      toast({
        title: 'Maximum reached',
        description: `Maximum ${maxImages} images allowed`,
        variant: 'destructive',
      });
      return;
    }
    onImagesUpdated([...existingImages, url]);
    toast({
      title: 'Image linked',
      description: 'Save the unit to keep the change.',
    });
    setPickerOpen(false);
  };

  const handleAddByUrl = () => {
    const url = addByUrlValue.trim();
    if (!url) {
      toast({
        title: 'Enter a URL',
        description: 'Paste an image URL to link it to this unit.',
        variant: 'destructive',
      });
      return;
    }
    try {
      new URL(url);
    } catch {
      toast({
        title: 'Invalid URL',
        description: 'Please enter a valid URL (e.g. https://...).',
        variant: 'destructive',
      });
      return;
    }
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      toast({
        title: 'Invalid URL',
        description: 'URL must start with http:// or https://',
        variant: 'destructive',
      });
      return;
    }
    if (existingImages.includes(url)) {
      toast({
        title: 'Already added',
        description: 'This URL is already in the list.',
        variant: 'destructive',
      });
      return;
    }
    if (existingImages.length >= maxImages) {
      toast({
        title: 'Maximum reached',
        description: `Maximum ${maxImages} images allowed`,
        variant: 'destructive',
      });
      return;
    }
    onImagesUpdated([...existingImages, url]);
    setAddByUrlValue('');
    toast({
      title: 'Image linked',
      description: 'Existing image URL added. Save the unit to keep the change.',
    });
  };

  /**
   * Upload and optimize image via local optimizer (development)
   * Calls localhost:3001 directly, bypassing Supabase Edge Function
   */
  const uploadViaLocalOptimizer = async (
    file: File,
    imageName: string
  ): Promise<OptimizeResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('unitId', unitId);
    formData.append('imageType', imageType);
    formData.append('imageName', imageName);

    console.log('Using local optimizer:', LOCAL_OPTIMIZER_URL);

    const response = await fetch(`${LOCAL_OPTIMIZER_URL}/optimize`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.error || `Local optimizer error: ${response.status}`);
    }

    return response.json();
  };

  /**
   * Upload and optimize image via Supabase Edge Function (production)
   * Edge Function forwards to Railway image optimizer service
   */
  const uploadViaEdgeFunction = async (
    file: File,
    imageName: string
  ): Promise<OptimizeResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('unitId', unitId);
    formData.append('imageType', imageType);
    formData.append('imageName', imageName);

    const { data, error } = await supabase.functions.invoke('optimize-unit-image', {
      body: formData,
    });

    if (error) {
      throw error;
    }

    return data as OptimizeResponse;
  };

  /**
   * Process files for upload (shared by file input and drag-drop)
   */
  const processFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;

    // Filter to only image files
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      toast({
        title: 'Invalid files',
        description: 'Please upload image files only',
        variant: 'destructive',
      });
      return;
    }

    if (existingImages.length + imageFiles.length > maxImages) {
      toast({
        title: 'Too many images',
        description: `Maximum ${maxImages} images allowed`,
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);
    setProgress(0);

    // Log which optimizer mode we're using
    const optimizerMode = LOCAL_OPTIMIZER_URL ? 'local' : 'edge-function';
    console.log(`Image upload mode: ${optimizerMode}`);

    try {
      const uploadedUrls: string[] = [];

      for (let i = 0; i < imageFiles.length; i++) {
        const file = imageFiles[i];
        const imageName = `${imageType}-${existingImages.length + i + 1}`;

        console.log('Uploading image:', { file: file.name, imageName, unitId, mode: optimizerMode });

        // Use local optimizer in development, Edge Function in production
        const data = LOCAL_OPTIMIZER_URL
          ? await uploadViaLocalOptimizer(file, imageName)
          : await uploadViaEdgeFunction(file, imageName);

        console.log('Upload response:', data);

        // Store the primary URL (use the largest size as the default)
        if (data?.images && data.images.length > 0) {
          // Find the largest image (highest width)
          const largestImage = data.images.reduce((prev, curr) => 
            curr.width > prev.width ? curr : prev
          );
          
          if (largestImage?.url) {
            uploadedUrls.push(largestImage.url);
          }
        }

        setProgress(((i + 1) / imageFiles.length) * 100);
      }

      // Update parent component with new images
      onImagesUpdated([...existingImages, ...uploadedUrls]);

      toast({
        title: 'Success',
        description: `Uploaded ${imageFiles.length} image(s)`,
      });
    } catch (error: unknown) {
      console.error('Upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload images';
      toast({
        title: 'Upload failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [existingImages, maxImages, imageType, unitId, onImagesUpdated, toast]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    await processFiles(files);
    // Reset the input
    e.target.value = '';
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading && existingImages.length < maxImages) {
      setIsDragging(true);
    }
  }, [uploading, existingImages.length, maxImages]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (uploading || existingImages.length >= maxImages) {
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    await processFiles(files);
  }, [uploading, existingImages.length, maxImages, processFiles]);

  const removeImage = (index: number) => {
    const newImages = existingImages.filter((_, i) => i !== index);
    onImagesUpdated(newImages);
    
    toast({
      title: 'Image removed',
      description: 'Image removed from gallery',
    });
  };

  const displayLabel = label || (imageType === 'gallery' ? 'Gallery Images' : 'Floor Plan');

  const getFullImageUrl = (linkedImage: LinkedImage): string => {
    if (linkedImage.imageUrls) {
      return getFallbackUrl(linkedImage.imageUrls);
    }
    return linkedImage.thumbnailUrl;
  };

  return (
    <div className="space-y-4">
      {/* Linked Images Section */}
      {linkedImages.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            Linked Architecture Drawings
            <span className="text-xs text-muted-foreground ml-2 font-normal">
              (Linked via unit_type)
            </span>
          </Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {linkedImages.map((linkedImage, index) => (
              <div
                key={index}
                className="relative group cursor-pointer"
                onClick={() => setSelectedLinkedImage(linkedImage)}
              >
                <div className="relative rounded-lg border-2 border-primary/30 bg-muted/30 overflow-hidden">
                  <img
                    src={linkedImage.thumbnailUrl}
                    alt={linkedImage.label}
                    className="w-full h-32 object-contain"
                  />
                  <div className="absolute top-2 left-2">
                    <Badge variant="secondary" className="text-xs">
                      <Link2 className="h-3 w-3 mr-1" />
                      Linked
                    </Badge>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1 truncate" title={linkedImage.label}>
                  {linkedImage.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <Label>
          {displayLabel}
          <span className="text-sm text-muted-foreground ml-2">
            ({existingImages.length}/{maxImages})
          </span>
        </Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading || existingImages.length >= maxImages}
          onClick={() => document.getElementById(`file-upload-${imageType}-${unitId}`)?.click()}
        >
          {uploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Upload
            </>
          )}
        </Button>
      </div>

      {/* Pick from storage or add by URL (link without re-upload) */}
      {existingImages.length < maxImages && (
        <div className="flex flex-wrap gap-2 items-center">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onOpenPicker}
            disabled={pickerLoading}
          >
            <FolderOpen className="mr-2 h-4 w-4" />
            Pick from storage
          </Button>
          <div className="flex gap-2 items-center flex-1 min-w-[200px]">
            <Input
              type="url"
              placeholder="Or paste image URL"
              value={addByUrlValue}
              onChange={(e) => setAddByUrlValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddByUrl())}
              className="flex-1 max-w-sm"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddByUrl}
              disabled={!addByUrlValue.trim()}
            >
              Add URL
            </Button>
          </div>
        </div>
      )}

      {/* Storage picker dialog */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Pick image from storage</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 flex-1 min-h-0 flex flex-col">
            <div className="flex items-center gap-2">
              <Label className="text-sm shrink-0">Folder</Label>
              <Select
                value={pickerFolder}
                onValueChange={onPickerFolderChange}
                disabled={pickerLoading || pickerFolders.length === 0}
              >
                <SelectTrigger className="w-[240px]">
                  <SelectValue placeholder="Select folder" />
                </SelectTrigger>
                <SelectContent>
                  {pickerFolders.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {pickerLoading ? (
              <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Loading…</span>
              </div>
            ) : (
              <ScrollArea className="flex-1 min-h-[240px] rounded-md border p-3">
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {pickerFiles.map(({ url, name }) => (
                    <button
                      type="button"
                      key={url}
                      className="rounded-lg border overflow-hidden hover:ring-2 hover:ring-primary focus:ring-2 focus:ring-primary focus:outline-none text-left"
                      onClick={() => handlePickImage(url)}
                    >
                      <img
                        src={url}
                        alt={name}
                        className="w-full aspect-video object-cover"
                      />
                      <p className="text-xs truncate px-2 py-1 bg-muted" title={name}>
                        {name}
                      </p>
                    </button>
                  ))}
                </div>
                {pickerFiles.length === 0 && pickerFolder && !pickerLoading && (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    No images in this folder.
                  </p>
                )}
              </ScrollArea>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Input
        id={`file-upload-${imageType}-${unitId}`}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        multiple={imageType === 'gallery' || imageType === 'floor_plan' || imageType === 'render'}
        onChange={handleFileUpload}
        className="hidden"
      />

      {uploading && (
        <div className="space-y-2">
          <Progress value={progress} />
          <p className="text-sm text-muted-foreground text-center">
            Optimizing and uploading... {Math.round(progress)}%
          </p>
        </div>
      )}

      {/* Sortable Image Grid */}
      {existingImages.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Drag images to reorder. Changes will be saved when you save the unit.
          </p>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={existingImages} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {existingImages.map((url, index) => (
                  <SortableImageItem
                    key={url}
                    id={url}
                    url={url}
                    index={index}
                    displayLabel={displayLabel}
                    onRemove={removeImage}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}

      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => {
          if (!uploading && existingImages.length < maxImages) {
            document.getElementById(`file-upload-${imageType}-${unitId}`)?.click();
          }
        }}
        className={`
          flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg transition-colors
          ${isDragging 
            ? 'border-primary bg-primary/10' 
            : 'border-border bg-muted/20 hover:border-primary/50'
          }
          ${!uploading && existingImages.length < maxImages ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}
        `}
      >
        <ImageIcon className={`h-12 w-12 mb-2 transition-colors ${isDragging ? 'text-primary' : 'text-muted-foreground'}`} />
        <p className={`text-sm text-center transition-colors ${isDragging ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
          {isDragging 
            ? 'Drop images here to upload' 
            : existingImages.length === 0
              ? `Drag and drop ${imageType === 'gallery' ? 'gallery images' : 'floor plans'} here, or click to browse`
              : existingImages.length < maxImages
                ? 'Drag and drop more images here, or click to browse'
                : `Maximum ${maxImages} images reached`
          }
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Supports JPEG, PNG, WebP
        </p>
      </div>

      {/* Linked Image View Dialog */}
      <Dialog open={!!selectedLinkedImage} onOpenChange={(open) => !open && setSelectedLinkedImage(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedLinkedImage?.label || 'Architecture Drawing'}</DialogTitle>
          </DialogHeader>
          {selectedLinkedImage && (
            <div className="mt-4">
              {selectedLinkedImage.imageUrls ? (
                <picture>
                  {selectedLinkedImage.imageUrls['640_avif'] || selectedLinkedImage.imageUrls['1280_avif'] || selectedLinkedImage.imageUrls['1920_avif'] ? (
                    <source
                      type="image/avif"
                      srcSet={[
                        selectedLinkedImage.imageUrls['640_avif'] && `${selectedLinkedImage.imageUrls['640_avif']} 640w`,
                        selectedLinkedImage.imageUrls['1280_avif'] && `${selectedLinkedImage.imageUrls['1280_avif']} 1280w`,
                        selectedLinkedImage.imageUrls['1920_avif'] && `${selectedLinkedImage.imageUrls['1920_avif']} 1920w`,
                      ].filter(Boolean).join(', ')}
                    />
                  ) : null}
                  {selectedLinkedImage.imageUrls['640_webp'] || selectedLinkedImage.imageUrls['1280_webp'] || selectedLinkedImage.imageUrls['1920_webp'] ? (
                    <source
                      type="image/webp"
                      srcSet={[
                        selectedLinkedImage.imageUrls['640_webp'] && `${selectedLinkedImage.imageUrls['640_webp']} 640w`,
                        selectedLinkedImage.imageUrls['1280_webp'] && `${selectedLinkedImage.imageUrls['1280_webp']} 1280w`,
                        selectedLinkedImage.imageUrls['1920_webp'] && `${selectedLinkedImage.imageUrls['1920_webp']} 1920w`,
                      ].filter(Boolean).join(', ')}
                    />
                  ) : null}
                  <img
                    src={getFallbackUrl(selectedLinkedImage.imageUrls)}
                    alt={selectedLinkedImage.label}
                    className="w-full h-auto rounded-lg"
                  />
                </picture>
              ) : (
                <img
                  src={selectedLinkedImage.thumbnailUrl}
                  alt={selectedLinkedImage.label}
                  className="w-full h-auto rounded-lg"
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
